import React from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
  alpha,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Compare as CompareIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { MoleMappingData } from '../types';

interface MappingSessionSidebarProps {
  mappings: MoleMappingData[];
  selectedMapping: MoleMappingData | null;
  loading: boolean;
  baselineSessions: any[];
  onSelectMapping: (mapping: MoleMappingData) => void;
  onDeleteMapping: (mapping: MoleMappingData) => void;
  onNewBaseline: () => void;
  onNewFollowup: () => void;
  onCompare: () => void;
}

const MappingSessionSidebar: React.FC<MappingSessionSidebarProps> = ({
  mappings,
  selectedMapping,
  loading,
  baselineSessions,
  onSelectMapping,
  onDeleteMapping,
  onNewBaseline,
  onNewFollowup,
  onCompare
}) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="Mole Mapping Sessions"
        action={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={onNewBaseline}
              variant="contained"
              size="small"
              fullWidth
            >
              New Baseline
            </Button>
            
            <Button
              startIcon={<TimelineIcon />}
              onClick={onNewFollowup}
              variant="outlined"
              size="small"
              disabled={baselineSessions.length === 0}
              fullWidth
            >
              Follow-up
            </Button>
            
            <Button
              startIcon={<CompareIcon />}
              onClick={onCompare}
              variant="text"
              size="small"
              color="secondary"
              fullWidth
              disabled={baselineSessions.length === 0}
            >
              Compare
            </Button>
          </Box>
        }
      />
      <CardContent>
        {loading && mappings.length === 0 ? (
          <CircularProgress size={24} sx={{ display: 'block', mx: 'auto' }} />
        ) : mappings.length === 0 ? (
          <Typography color="text.secondary">
            No mole mapping sessions found. Create your first session to get started.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {mappings.map(mapping => (
              <Paper
                key={mapping.id}
                sx={{
                  p: 2,
                  mb: 1,
                  cursor: 'pointer',
                  bgcolor: selectedMapping?.id === mapping.id 
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'background.paper',
                  borderLeft: selectedMapping?.id === mapping.id 
                    ? `4px solid ${theme.palette.primary.main}`
                    : '4px solid transparent',
                  position: 'relative',
                  '&:hover .session-actions': {
                    opacity: 1
                  }
                }}
                onClick={() => onSelectMapping(mapping)}
              >
                <Typography variant="subtitle2">
                  {dayjs(mapping.sessionDate).format('MMMM D, YYYY')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(mapping.createdAt).format('MMM D, YYYY h:mm A')}
                </Typography>
                {mapping.notes && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {mapping.notes}
                  </Typography>
                )}
                
                {/* Delete button */}
                <Box
                  className="session-actions"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: 0,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.error.main, 0.9),
                      color: '#fff',
                      width: 24,
                      height: 24,
                      '&:hover': {
                        backgroundColor: theme.palette.error.main
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMapping(mapping);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MappingSessionSidebar;
